import React from "react";
import {
  StyleSheet,
  View,
  Modal,
  Pressable,
  TouchableOpacity,
  StyleProp,
  ViewStyle,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "./ThemedText";
import { BrandColors } from "@/constants/ThemeColours";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useComponentTheme } from "@/hooks/useComponentTheme";
import {
  calculatePopoverPosition,
  type IPopoverAnchor,
  type IPopoverLayout,
} from "@/utils/popover-positioning";

/**
 * Props for the ThemedPopupScreen component.
 */
export interface IThemedPopupScreenProps {
  /** Whether the popup screen is visible */
  visible: boolean;
  /** Callback fired when the user dismisses the popup (via outside tap or close button) */
  onClose: () => void;
  /** Optional contextual coordinates of cursor or trigger element */
  anchorPosition?: IPopoverAnchor | null;
  /** Optional title displayed in header */
  title?: string;
  /** Optional icon displayed next to title in header */
  icon?: keyof typeof Ionicons.glyphMap;
  /** Optional subtitle or counter label */
  subtitle?: string;
  /** Target width for the popover card (default: 440) */
  width?: number;
  /** Target maximum height for the popover card (default: 500) */
  maxHeight?: number;
  /** Viewport margin in pixels (default: 12) */
  margin?: number;
  /** Vertical offset from anchor point in pixels (default: 8) */
  offset?: number;
  /** Optional custom node in header right (alongside close button) */
  headerRight?: React.ReactNode;
  /** Optional footer content placed at bottom of card */
  footer?: React.ReactNode;
  /** Whether to render the standard close [X] button (default: true) */
  showCloseButton?: boolean;
  /** Children rendered within the popover card body */
  children: React.ReactNode;
  /** Optional testID for testing */
  testID?: string;
  /** Modal animation type (default: "fade") */
  animationType?: "fade" | "none";
  /** Optional style override for floating hover card */
  cardStyle?: StyleProp<ViewStyle>;
  /** Optional style override for body content container */
  contentContainerStyle?: StyleProp<ViewStyle>;
}

/**
 * Type alias for PopupScreen props.
 */
export type IPopupScreenProps = IThemedPopupScreenProps;

/**
 * Shared contextual popup container for pickers and flyout workflows (DatePicker, Map, TagPicker,
 * MemoryPicker, MediaPicker, ContactPicker, GroupPicker).
 *
 * Distinct from centered blocking modals:
 * - Appears adjacent to user cursor/trigger via `calculatePopoverPosition`
 * - Retains full underlying view visibility with a transparent dismiss backdrop
 * - Provides standardized Archival Blue Linen card chrome, high-contrast borders, and close affordance
 */
export function ThemedPopupScreen({
  visible,
  onClose,
  anchorPosition,
  title,
  icon,
  subtitle,
  width = 440,
  maxHeight = 500,
  margin = 12,
  offset = 8,
  headerRight,
  footer,
  showCloseButton = true,
  children,
  testID,
  animationType = "fade",
  cardStyle,
  contentContainerStyle,
}: IThemedPopupScreenProps) {
  const { popupMenu } = useComponentTheme();
  const cardBg = useThemeColor({}, "card");
  const defaultBorder = BrandColors.blueLinen.hex;
  const borderColor = popupMenu.card.borderColor || defaultBorder;
  const textMuted = popupMenu.header.subtitleColor;

  const cardRef = React.useRef<View>(null);

  React.useEffect(() => {
    if (!visible || Platform.OS !== "web" || typeof document === "undefined") return;

    const handleDocumentClick = (e: MouseEvent | TouchEvent) => {
      const node = cardRef.current as any;
      const target = e.target as HTMLElement | null;
      if (node && target) {
        const domNode = node instanceof HTMLElement ? node : (node?._nativeNode || node?.node || null);
        if (domNode && typeof domNode.contains === "function" && !domNode.contains(target)) {
          setTimeout(() => {
            onClose();
          }, 0);
        }
      }
    };

    document.addEventListener("mousedown", handleDocumentClick, true);
    document.addEventListener("touchstart", handleDocumentClick, true);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick, true);
      document.removeEventListener("touchstart", handleDocumentClick, true);
    };
  }, [visible, onClose]);

  if (!visible) return null;

  const layout: IPopoverLayout = calculatePopoverPosition({
    anchorPosition,
    popoverWidth: width,
    popoverHeight: maxHeight,
    margin,
    offset,
  });

  const hasHeader = Boolean(title || icon || subtitle || headerRight || showCloseButton);

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType={animationType}
      onRequestClose={onClose}
    >
      <View style={styles.modalRoot} pointerEvents="box-none">
        {/* Full-screen invisible backdrop to dismiss popup when clicking outside */}
        <Pressable
          style={styles.outsideDismiss}
          onPress={onClose}
          pointerEvents="auto"
          testID={testID ? `${testID}-backdrop` : "popup-backdrop"}
          accessibilityLabel="Dismiss popup"
        />

        {/* Floating Contextual Hover Card */}
        <View
          ref={cardRef}
          style={[
            styles.hoverCard,
            {
              top: layout.top,
              left: layout.left,
              width: layout.width,
              maxHeight: layout.maxHeight,
              backgroundColor: cardBg || BrandColors.blueLinen.hex,
              borderColor,
              borderRadius: popupMenu.card.popupRadius,
            },
            cardStyle,
          ]}
          testID={testID ? `${testID}-card` : "popup-card"}
        >
          {/* Standardized Header */}
          {hasHeader && (
            <View style={[styles.header, { borderBottomColor: popupMenu.header.borderBottomColor }]}>
              <View style={styles.headerTitleRow}>
                {icon && (
                  <Ionicons
                    name={icon}
                    size={18}
                    color={popupMenu.header.iconColor}
                    style={{ marginRight: 6 }}
                  />
                )}
                <View>
                  {title && (
                    <ThemedText
                      type="defaultSemiBold"
                      style={[styles.headerTitle, { color: popupMenu.header.titleColor }]}
                      numberOfLines={1}
                    >
                      {title}
                    </ThemedText>
                  )}
                  {subtitle && (
                    <ThemedText style={[styles.subtitle, { color: popupMenu.header.subtitleColor }]}>
                      {subtitle}
                    </ThemedText>
                  )}
                </View>
              </View>

              <View style={styles.headerRightRow}>
                {headerRight}
                {showCloseButton && (
                  <TouchableOpacity
                    onPress={onClose}
                    style={[styles.closeBtn, { backgroundColor: popupMenu.header.closeButtonBg }]}
                    accessibilityLabel="Close popup"
                    accessibilityRole="button"
                    testID={testID ? `${testID}-close-btn` : "popup-close-btn"}
                  >
                    <Ionicons name="close" size={18} color={popupMenu.header.closeButtonIconColor} />
                  </TouchableOpacity>
                )}
              </View>
            </View>
          )}

          {/* Body Content */}
          <View style={[styles.bodyContent, contentContainerStyle]}>
            {children}
          </View>

          {/* Optional Footer */}
          {footer && (
            <View style={[styles.footer, { borderTopColor: popupMenu.footer.borderTopColor, backgroundColor: popupMenu.footer.backgroundColor }]}>
              {footer}
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
}

/**
 * Convenient alias for ThemedPopupScreen.
 */
export const PopupScreen = ThemedPopupScreen;

const styles = StyleSheet.create({
  modalRoot: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 9999,
  },
  outsideDismiss: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "transparent",
    cursor: "default" as any,
  },

  hoverCard: {
    position: "absolute",
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: "rgba(28, 35, 51, 0.25)",
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.22,
    shadowRadius: 16,
    elevation: 12,
    zIndex: 10000,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderBottomWidth: 1,
    minHeight: 38,
  },
  headerTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    marginRight: 8,
  },
  headerTitle: {
    fontSize: 14,
    color: "#161F36",
    lineHeight: 18,
  },
  subtitle: {
    fontSize: 10,
    lineHeight: 13,
  },
  headerRightRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  closeBtn: {
    width: 24,
    height: 24,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(28, 35, 51, 0.08)",
  },
  bodyContent: {
    flex: 1,
    minHeight: 0,
  },
  footer: {
    borderTopWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: "rgba(28, 35, 51, 0.04)",
  },
});

